/**
 * Catálogos de estatus POR MODALIDAD.
 *
 * Cada modalidad tiene sus propios nombres y etapas (un barco "zarpa" y un avión
 * "despega"), pero todos se comportan igual: alimentan las status cards, el
 * filtro rápido y el pill de la tabla, tal como en Loads.
 *
 * Cuando exista el backend, estos catálogos se reemplazan por lo que devuelva la
 * BD; la forma de los datos se mantiene igual.
 */

import { ShipmentMode, ShipmentStage } from './types';

export interface StatusDef {
  /** Valor exacto que se guarda/devuelve como estatus. */
  key: string;
  /** Etiqueta a mostrar (se pinta en MAYÚSCULAS por CSS). */
  label: string;
  color: string;
  /** Etapa normalizada a la que pertenece este estatus. */
  stage: ShipmentStage;
  /**
   * Modalidades a las que pertenece el estatus. Solo se usa en Global View, para
   * distinguir de dónde viene cada card (varios estatus comparten nombre entre
   * modalidades, por ejemplo "Booked" o "Cancelled").
   */
  modes?: ShipmentMode[];
}

export const TOTAL_COLOR = '#3a3f45';

/* Paleta compartida para que las 3 modalidades se lean igual */
const C = {
  pending: '#f5a623',
  waiting: '#00a2ae',
  transit: '#2f9fd0',
  arrived: '#5cb85c',
  done: '#22a04a',
  cancelled: '#ef4444',
};

/** Terrestre: se conserva el catálogo actual de Loads, sin cambios. */
const LAND_STATUSES: StatusDef[] = [
  { key: 'Unassigned', label: 'Unassigned', color: C.pending, stage: 'origin' },
  { key: 'Assigned', label: 'Assigned', color: C.pending, stage: 'origin' },
  { key: 'Waiting to be loaded', label: 'Waiting Load', color: C.waiting, stage: 'origin' },
  { key: 'On Transit', label: 'On Transit', color: C.transit, stage: 'in_transit' },
  { key: 'At Destination', label: 'At Destination', color: C.arrived, stage: 'destination' },
  { key: 'Completed', label: 'Completed', color: C.done, stage: 'delivered' },
  { key: 'Dry run', label: 'Dry Run', color: C.cancelled, stage: 'cancelled' },
];

/**
 * Marítimo: alineado a los estatus que reportan las navieras vía DCSA
 * (booked → loaded → sailing → arrived → discharged).
 */
const OCEAN_STATUSES: StatusDef[] = [
  { key: 'Booked', label: 'Booked', color: C.pending, stage: 'origin' },
  { key: 'Gate In', label: 'Gate In', color: C.pending, stage: 'origin' },
  { key: 'Loaded', label: 'Loaded', color: C.waiting, stage: 'origin' },
  { key: 'Sailing', label: 'Sailing', color: C.transit, stage: 'in_transit' },
  { key: 'Arrived', label: 'Arrived', color: C.arrived, stage: 'destination' },
  // ⚠️ Descargado del buque NO es entregado: el contenedor sigue en la terminal
  // del puerto de destino. Por eso `Discharged` es etapa `destination` (abierta)
  // y el estatus final es `Delivered` (salida de la terminal tras la descarga).
  { key: 'Discharged', label: 'Discharged', color: C.arrived, stage: 'destination' },
  { key: 'Delivered', label: 'Delivered', color: C.done, stage: 'delivered' },
  { key: 'Cancelled', label: 'Cancelled', color: C.cancelled, stage: 'cancelled' },
];

/** Aéreo: alineado a los códigos de evento IATA (RCS/MAN/DEP/ARR/RCF/DLV). */
const AIR_STATUSES: StatusDef[] = [
  { key: 'Booked', label: 'Booked', color: C.pending, stage: 'origin' },
  { key: 'Received', label: 'Received', color: C.pending, stage: 'origin' },
  { key: 'Manifested', label: 'Manifested', color: C.waiting, stage: 'origin' },
  { key: 'In Flight', label: 'In Flight', color: C.transit, stage: 'in_transit' },
  { key: 'Landed', label: 'Landed', color: C.arrived, stage: 'destination' },
  { key: 'Delivered', label: 'Delivered', color: C.done, stage: 'delivered' },
  { key: 'Cancelled', label: 'Cancelled', color: C.cancelled, stage: 'cancelled' },
];

export const STATUS_CATALOGS: Record<ShipmentMode, StatusDef[]> = {
  land: LAND_STATUSES,
  ocean: OCEAN_STATUSES,
  air: AIR_STATUSES,
};

/** Token canónico de un estatus: tolera variantes de nombre y mayúsculas. */
export const canonStatus = (name?: string): string =>
  (name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');

/** Busca la definición de un estatus dentro de una modalidad. */
export const findStatusDef = (
  mode: ShipmentMode,
  status?: string
): StatusDef | undefined => {
  const token = canonStatus(status);
  return STATUS_CATALOGS[mode].find((d) => canonStatus(d.key) === token);
};

/** Color del pill de estatus en la tabla. */
export const statusColor = (mode: ShipmentMode, status?: string): string =>
  findStatusDef(mode, status)?.color || '#8a8f98';

/** Gris de "sin clasificar": mismo valor que usa `statusColor` cuando no encuentra el estatus. */
export const UNKNOWN_COLOR = '#8a8f98';

/**
 * Color por ETAPA, para cuando no hay un estatus de catálogo que empatar.
 *
 * 🔑 Hace falta porque Track Shipment muestra el **texto libre del proveedor**
 * (`statusLabel`), no el estatus del catálogo: cada transportista titula distinto ("En Route",
 * "On Board Vessel", "Sailing"…) y `findStatusDef` solo empata nombres exactos. La **etapa** sí está
 * normalizada, así que sirve de red: un estatus que no reconocemos igual se pinta con el color de su
 * momento del viaje, en lugar de caer todo al gris.
 */
export const STAGE_COLORS: Record<ShipmentStage, string> = {
  origin: C.pending,
  in_transit: C.transit,
  destination: C.arrived,
  delivered: C.done,
  cancelled: C.cancelled,
  unknown: UNKNOWN_COLOR,
};

/**
 * Para Global View: se busca el estatus en las 3 modalidades, porque la vista
 * consolidada mezcla registros de todas.
 */
export const statusColorAnyMode = (status?: string): string => {
  const token = canonStatus(status);
  for (const defs of Object.values(STATUS_CATALOGS)) {
    const hit = defs.find((d) => canonStatus(d.key) === token);
    if (hit) return hit.color;
  }
  return '#8a8f98';
};

/* ------------------------------------------------------------------ */
/* Vista RESUMIDA de las status cards                                  */
/* ------------------------------------------------------------------ */

/**
 * Agrupa los estatus por ETAPA en 4 bloques.
 *
 * Necesario porque en Global View se acumulan los estatus de las 3 modalidades
 * y las cards ya no caben (aparecía scroll horizontal, que rompe la estética).
 * Al agrupar por etapa siempre son 4 cards + Total, sin importar cuántos
 * estatus existan ni cuántas modalidades se agreguen después.
 */
export interface StageGroup {
  key: string;
  label: string;
  color: string;
  stages: ShipmentStage[];
}

export const STAGE_GROUPS: StageGroup[] = [
  {
    key: 'group:upcoming',
    label: 'Upcoming',
    color: C.pending,
    stages: ['origin'],
  },
  {
    key: 'group:transit',
    label: 'In Transit',
    color: C.transit,
    stages: ['in_transit'],
  },
  {
    key: 'group:completed',
    label: 'Completed',
    color: C.done,
    // Incluye "en destino" (por finalizar) y "entregado" (finalizado)
    stages: ['destination', 'delivered'],
  },
  {
    key: 'group:cancelled',
    label: 'Cancelled',
    color: C.cancelled,
    stages: ['cancelled'],
  },
];

/** Etapa a la que pertenece un estatus, buscando en todas las modalidades. */
export const stageOfStatus = (status?: string): ShipmentStage => {
  const token = canonStatus(status);
  for (const defs of Object.values(STATUS_CATALOGS)) {
    const hit = defs.find((d) => canonStatus(d.key) === token);
    if (hit) return hit.stage;
  }
  return 'unknown';
};

/** Grupo al que pertenece un estatus (o undefined si no encaja en ninguno). */
export const groupOfStatus = (status?: string): StageGroup | undefined => {
  const stage = stageOfStatus(status);
  return STAGE_GROUPS.find((g) => g.stages.includes(stage));
};

/**
 * Convierte los grupos a `StatusDef` para que las status cards no necesiten
 * saber si están mostrando estatus individuales o agrupados.
 */
export const stageGroupsAsDefs = (): StatusDef[] =>
  STAGE_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    color: g.color,
    stage: g.stages[0],
  }));

/**
 * A qué modalidades pertenece un estatus, según los CATÁLOGOS (no según los
 * datos). Es importante que sea así: derivarlo de los registros hacía que un
 * estatus sin embarques no mostrara distintivo, cuando igual pertenece a su
 * modalidad.
 */
export const modesOfStatus = (status?: string): ShipmentMode[] => {
  const token = canonStatus(status);
  return (Object.keys(STATUS_CATALOGS) as ShipmentMode[]).filter((mode) =>
    STATUS_CATALOGS[mode].some((d) => canonStatus(d.key) === token)
  );
};
